import React, { useEffect, useState } from "react";
import { getExchangeRates, calculateTax } from "../api/tariffApi";
import {
  X,
  Calculator,
  Loader2,
  Receipt,
  CircleDollarSign,
} from "lucide-react";

// --- INTERFACES ---
interface TariffDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  tariffItem: any | null;
}

interface CalculatedLevies {
  customs_duty: number;
  customs_duty_detail?: string;
  cess: number;
  cess_detail?: string;
  cess_sg: number;
  cess_sg_detail?: string;
  pal: number;
  pal_detail?: string;
  pal_sg: number;
  pal_sg_detail?: string;
  surcharge: number;
  surcharge_detail?: string;
  excise: number;
  excise_detail?: string;
  vat: number;
  vat_detail?: string;
  sscl: number;
  sscl_detail?: string;
  scl: number;
  scl_detail?: string;
  luxury_tax: number;
  luxury_tax_detail?: string;
  total_levies: number;
  total_with_cif: number;
  cif_value_lkr: number;
}

interface UnitCandidate {
  type: string;
  key: string;
  rate: number;
}

const getUnitCandidates = (item: any): UnitCandidate[] => {
  if (!item) return [];
  const candidates: UnitCandidate[] = [];
  const addedTypes = new Set<string>();

  for (const [key, value] of Object.entries(item)) {
    if (value === null || value === undefined || value === "") {
      continue;
    }

    const lowerKey = String(key).toLowerCase();
    const valueStr = String(value).toLowerCase();

    // Extract numeric rate from value
    const rateMatch = valueStr.match(/(\d[\d,]*\.?\d*)/);
    const rate = rateMatch ? parseFloat(rateMatch[1].replace(/,/g, '')) : 0;

    if (rate <= 0) {
      continue;
    }

    // Sugar detection
    if ((lowerKey.includes("sugar") || valueStr.includes("sugar")) && !addedTypes.has("sugar")) {
      candidates.push({ key, rate, type: "sugar" });
      addedTypes.add("sugar");
    }
    // MPR/MRP detection
    else if ((/\bmrp\b|\bmpr\b/.test(lowerKey) || /\bmrp\b|\bmpr\b/.test(valueStr)) && !addedTypes.has("mpr")) {
      candidates.push({ key, rate, type: "mpr" });
      addedTypes.add("mpr");
    }
    // CM3 detection
    else if ((lowerKey.includes("cm3") || lowerKey.includes("cm³") || valueStr.includes("cm3") || valueStr.includes("cm³")) && !addedTypes.has("cm3")) {
      const cm3Match = valueStr.replace(/,/g, '').match(/rs\.?\s*(\d+\.?\d*)[^a-z0-9]*per\s*cm/i);
      const cm3Rate = cm3Match ? parseFloat(cm3Match[1]) : rate;
      candidates.push({ key, rate: cm3Rate, type: "cm3" });
      addedTypes.add("cm3");
    }
    // CC detection (not CM3)
    else if ((/\bcc\b/.test(lowerKey) || /\bcc\b/.test(valueStr)) && !lowerKey.includes("cm3") && !addedTypes.has("cc")) {
      const ccMatch = valueStr.replace(/,/g, '').match(/rs\.?\s*(\d+\.?\d*)[^a-z0-9]*per\s*cc/i);
      const ccRate = ccMatch ? parseFloat(ccMatch[1]) : rate;
      candidates.push({ key, rate: ccRate, type: "cc" });
      addedTypes.add("cc");
    }
    // General unit rate
    else if ((lowerKey.includes("unit rate") || lowerKey.includes("customs duty unit")) && !addedTypes.has("general")) {
      candidates.push({ key, rate, type: "general" });
      addedTypes.add("general");
    }
  }

  return candidates;
};

// --- MAIN COMPONENT ---
const TariffDetailModal: React.FC<TariffDetailModalProps> = ({
  isOpen,
  onClose,
  tariffItem,
}) => {
  const [dutySelection, setDutySelection] = useState<"GEN" | "PREF">("GEN");
  const [isCalcFormOpen, setIsCalcFormOpen] = useState(false);
  const [isBreakdownOpen, setIsBreakdownOpen] = useState(false);
  const [cifValue, setCifValue] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [sugarQuantity, setSugarQuantity] = useState<number | null>(null);
  const [mprQuantity, setMprQuantity] = useState<number | null>(null);
  const [ccQuantity, setCcQuantity] = useState<number | null>(null);
  const [cm3Quantity, setCm3Quantity] = useState<number | null>(null);
  const [calculatedLevies, setCalculatedLevies] =
    useState<CalculatedLevies | null>(null);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [inputCurrency, setInputCurrency] = useState<string>("USD");
  const [isCalculating, setIsCalculating] = useState(false);
  const [unitCandidates, setUnitCandidates] = useState<UnitCandidate[]>([]);
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({
    "LKR": 1.0,
    "USD": 300.0,
    "EUR": 335.0,
    "AUD": 200.0
  });


  // Dynamic currencies list based on fetched rates
  const currencies: { [key: string]: { symbol: string; rate: number } } = Object.entries(exchangeRates).reduce(
    (acc, [key, rate]) => ({
      ...acc,
      [key]: { symbol: key, rate: rate }
    }),
    {}
  );

  useEffect(() => {
    if (!isOpen) {
      setIsCalcFormOpen(false);
      setIsBreakdownOpen(false);
      setCifValue(null);
      setQuantity(null);
      setSugarQuantity(null);
      setMprQuantity(null);
      setCcQuantity(null);
      setCm3Quantity(null);
      setCalculatedLevies(null);
      setCalculationError(null);
      setUnitCandidates([]);
    } else {
        // Fetch fresh rates when modal opens
        getExchangeRates().then(rates => {
            setExchangeRates(rates);
        });
        if (tariffItem) {
          setUnitCandidates(getUnitCandidates(tariffItem));
        }
    }
  }, [isOpen, tariffItem]);

  if (!isOpen || !tariffItem) return null;

  const hasGenDuty = tariffItem["Gen Duty"] && parseFloat(String(tariffItem["Gen Duty"]).replace('%', '')) > 0;

  const hasPreferentialDuty = [
    "AD",
    "BN",
    "GT",
    "IN",
    "PK",
    "SA",
    "SF",
    "SD",
    "SG",
    "Preferential Duty AP",
  ].some((k) => {
    const value = tariffItem[k];
    return value && parseFloat(String(value).replace('%', '')) > 0;
  });

  const exchangeRate = currencies[inputCurrency as keyof typeof currencies]?.rate || 1;
  const LKR_SYMBOL = "LKR";

  // --- BACKEND API CALL FOR CALCULATION ---
  const handleCalculate = async () => {
    const cifNum = Number(cifValue) || 0;
    
    if (cifNum <= 0) {
      setCalculationError("CIF Value must be greater than 0.");
      return;
    }

    setCalculationError(null);
    setIsCalculating(true);
    setCalculatedLevies(null);

    try {
      const data = await calculateTax({
        hs_code: tariffItem["HS Code"],
        cif_value: cifNum,
        currency: inputCurrency,
        duty_type: dutySelection,
        quantity: quantity || 0,
        sugar_quantity: sugarQuantity || 0,
        mpr_quantity: mprQuantity || 0,
        cc_quantity: ccQuantity || 0,
        cm3_quantity: cm3Quantity || 0,
      });
      
      // Helper function to safely convert to number
      const toNumber = (value: any): number => {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const cleaned = value.replace(/[^\d.-]/g, '');
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : parsed;
        }
        return 0;
      };
      
      const formatRate = (val: any) => {
        if (val === null || val === undefined) return "";
        const s = String(val).trim();
        if (s === "" || s.toLowerCase() === "nan" || s.toLowerCase() === "none") return "";
        if (/^\d+(\.\d+)?$/.test(s)) return `${s}%`;
        return s;
      };

      setCalculatedLevies({
        customs_duty: toNumber(data.customs_duty),
        customs_duty_detail: data.duty_type || data.general_duty_calculation_details,
        cess: toNumber(data.cess),
        cess_detail: data.cess_calculation_details || data.cess_raw_rate || formatRate(tariffItem["Cess Gen"] || tariffItem["Cess"]),
        cess_sg: toNumber(data.cess_sg),
        cess_sg_detail: formatRate(tariffItem["Cess SG"]),
        pal: toNumber(data.pal),
        pal_detail: formatRate(tariffItem["PAL Gen"] || tariffItem["PAL"]),
        pal_sg: toNumber(data.pal_sg),
        pal_sg_detail: formatRate(tariffItem["PAL SG"]),
        surcharge: toNumber(data.surcharge),
        surcharge_detail: formatRate(tariffItem["Surcharge on Customs Duty"] || tariffItem["Surcharge"]),
        excise: toNumber(data.excise),
        excise_detail: data.excise_calculation_details || data.excise_raw_rate,
        vat: toNumber(data.vat),
        vat_detail: formatRate(tariffItem["VAT"]),
        sscl: toNumber(data.sscl),
        sscl_detail: formatRate(tariffItem["SSCL"]),
        scl: toNumber(data.scl),
        scl_detail: formatRate(tariffItem["S C L"] || tariffItem["SCL"]),
        luxury_tax: toNumber(data.luxury_tax),
        luxury_tax_detail: data.luxury_tax_calculation_details || `${tariffItem["Luxury Tax_Rate on the amount exceeding threshold"] || tariffItem["Rate on the amount exceeding Luxury Tax Free Threshold"] || ""}`,
        total_levies: toNumber(data.total_levies),
        total_with_cif: toNumber(data.total_with_cif),
        cif_value_lkr: toNumber(data.cif_value_lkr),
      });

      if (data.unit_candidates_detected && Array.isArray(data.unit_candidates_detected)) {
        setUnitCandidates(data.unit_candidates_detected);
      }

      // Close form and open breakdown
      setIsCalcFormOpen(false);
      setIsBreakdownOpen(true);

    } catch (error) {
      console.error("Calculation error:", error);
      setCalculationError(
        error instanceof Error 
          ? error.message 
          : "Failed to calculate. Please check your connection and try again."
      );
    } finally {
      setIsCalculating(false);
    }
  };

  const hasSugarRate = unitCandidates.some((c) => c.type === "sugar");
  const hasMprRate = unitCandidates.some((c) => c.type === "mpr");
  const hasCcRate = unitCandidates.some((c) => c.type === "cc");
  const hasCm3Rate = unitCandidates.some((c) => c.type === "cm3");

  const renderTariffTable = () => {
    const EXCLUDE_KEYS = [
      "HS Hdg",
      "HS Code",
      "Description",
      "Unit",
      "ICL/SLSI",
      "764",
      "765",
      "80",
      "_search_text",
      "Source_page",
      "Source_Page",
      "Code",
    ];

    const LEVY_ORDER = [
      "ICL/SLSI",
      "Gen Duty",
      "AD",
      "BN",
      "GT",
      "IN",
      "PK",
      "SA",
      "SF",
      "SD",
      "SG",
      "Preferential Duty AP",
      "PAL Gen",
      "PAL SG",
      "Cess Gen",
      "Cess SG",
      "Excise (S.P.D)",
      "Surcharge on Customs Duty",
      "SSCL",
      "VAT",
      "S C L",
      "SCL",
      "Luxury Tax_Free threshold value",
      "Luxury Tax_Rate on the amount exceeding threshold",
    ];

    const allKeys = Object.keys(tariffItem || {}).filter(
      (key) =>
        !EXCLUDE_KEYS.includes(key.trim()) &&
        tariffItem[key] !== null &&
        tariffItem[key] !== undefined &&
        tariffItem[key] !== ""
    );

    const sortedKeys = allKeys.sort((a, b) => {
      const indexA = LEVY_ORDER.indexOf(a);
      const indexB = LEVY_ORDER.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return a.localeCompare(b);
    });

    const dataRows = sortedKeys.map((key) => ({
      label: key,
      value: tariffItem[key],
    }));

    return (
      <div className="tariff-table-wrapper">
        <table className="tariff-table">
          <thead>
            <tr>
              <th className="table-header-cell">Levy</th>
              <th className="table-header-cell">Rate / Value</th>
            </tr>
          </thead>
          <tbody className="table-body">
            {dataRows.map((row) => (
              <tr key={row.label} className="table-row">
                <td className="table-data-label">{row.label}</td>
                <td className="table-data-value">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="header-title">
            Tariff Details for{" "}
            <span className="hs-code-badge">{tariffItem["HS Code"]}</span>
          </h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="description-box">
            <h3 className="description-title">Product Description</h3>
            <p className="description-text">
              {tariffItem.Description || "No description provided."}
            </p>
          </div>

          <h3 className="table-heading">Applicable Levy Rates</h3>
          {renderTariffTable()}

          <div className="calculator-entry-point" style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid #e2e8f0" }}>
            <button 
              className="primary-btn"
              onClick={() => setIsCalcFormOpen(true)}
              style={{ padding: "12px 24px", display: "flex", alignItems: "center", gap: "10px", margin: "0 auto" }}
            >
              <Calculator size={20} />
              Open Duty Calculator
            </button>
          </div>

          {/* --- CALCULATOR FORM POPUP --- */}
          {isCalcFormOpen && (
            <div className="nested-modal-overlay">
              <div className="nested-modal-content">
                <div className="modal-header">
                  <h3 className="form-heading">Estimate Your Levies</h3>
                  <button className="close-button" onClick={() => setIsCalcFormOpen(false)}>
                    <X size={20} />
                  </button>
                </div>
                
                <div className="calculator-scroll-area">
                  <div className="input-grid-vertical">
                    {/* 1. CIF Value & Currency */}
                    <div className="input-row">
                      <div className="input-group full-width">
                        <label className="input-label">CIF Value & Currency</label>
                        <div className="combined-input">
                          <select
                            value={inputCurrency}
                            onChange={(e) => setInputCurrency(e.target.value)}
                            className="currency-select"
                          >
                            {Object.keys(currencies).map((curr) => (
                              <option key={curr} value={curr}>{curr}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            value={cifValue ?? ""}
                            placeholder="Enter CIF Value"
                            onChange={(e) => setCifValue(Number(e.target.value))}
                            className="main-value-input"
                          />
                        </div>
                        <p className="exchange-note">* 1 {inputCurrency} = {exchangeRate.toFixed(2)} LKR</p>
                      </div>
                    </div>

                    {/* 2. Duty Type Selection (if applicable) */}
                    {hasGenDuty && hasPreferentialDuty && (
                      <div className="input-row">
                        <div className="input-group full-width">
                          <label className="input-label">Customs Duty Type</label>
                          <div className="radio-group">
                            <label className="radio-item">
                              <input type="radio" name="dutyTypePop" value="GEN" checked={dutySelection === "GEN"} onChange={() => setDutySelection("GEN")} />
                              <span>General</span>
                            </label>
                            <label className="radio-item">
                              <input type="radio" name="dutyTypePop" value="PREF" checked={dutySelection === "PREF"} onChange={() => setDutySelection("PREF")} />
                              <span>Preferential</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 3. Quantity */}
                    <div className="input-row">
                      <div className="input-group full-width">
                        <label className="input-label">Quantity (Unit: {tariffItem.Unit || "N/A"})</label>
                        <input
                          type="number"
                          value={quantity ?? ""}
                          placeholder="Enter Quantity"
                          onChange={(e) => setQuantity(Number(e.target.value))}
                          className="text-input-field"
                        />
                      </div>
                    </div>

                    {/* 4. Other Dynamic Quantities */}
                    {(hasSugarRate || hasMprRate || hasCcRate || hasCm3Rate) && (
                      <div className="input-row grid-2-columns">
                        {hasSugarRate && (
                          <div className="input-group">
                            <label className="input-label">Sugar (Kg)</label>
                            <input type="number" value={sugarQuantity ?? ""} onChange={(e) => setSugarQuantity(Number(e.target.value))} className="text-input-field" />
                          </div>
                        )}
                        {hasMprRate && (
                          <div className="input-group">
                            <label className="input-label">MPR/MRP</label>
                            <input type="number" value={mprQuantity ?? ""} onChange={(e) => setMprQuantity(Number(e.target.value))} className="text-input-field" />
                          </div>
                        )}
                        {hasCcRate && (
                          <div className="input-group">
                            <label className="input-label">CC</label>
                            <input type="number" value={ccQuantity ?? ""} onChange={(e) => setCcQuantity(Number(e.target.value))} className="text-input-field" />
                          </div>
                        )}
                        {hasCm3Rate && (
                          <div className="input-group">
                            <label className="input-label">CM³</label>
                            <input type="number" value={cm3Quantity ?? ""} onChange={(e) => setCm3Quantity(Number(e.target.value))} className="text-input-field" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={handleCalculate}
                    className="calculate-btn-large"
                    disabled={isCalculating}
                  >
                    {isCalculating ? <Loader2 className="animate-spin" /> : "Calculate Levies"}
                  </button>

                  {calculationError && <p className="error-alert">{calculationError}</p>}
                </div>
              </div>
            </div>
          )}

          {/* --- BREAKDOWN RESULTS POPUP --- */}
          {isBreakdownOpen && calculatedLevies && (
            <div className="nested-modal-overlay">
              <div className="nested-modal-content results-modal">
                <div className="modal-header">
                  <h3 className="form-heading">Estimated Breakdown</h3>
                  <button className="close-button" onClick={() => setIsBreakdownOpen(false)}>
                    <X size={20} />
                  </button>
                </div>

                <div className="results-scroll-area">
                  <div className="breakdown-grid">
                    {(() => {
                      const isPresent = (v: any) => v !== null && v !== undefined && String(v).trim() !== "" && String(v).toLowerCase() !== "nan" && String(v).toLowerCase() !== "none";
                      
                      return [
                        { 
                          key: 'customs_duty', 
                          label: 'CUSTOMS DUTY',
                          hasRate: isPresent(tariffItem["Gen Duty"]) || isPresent(tariffItem["Preferential Duty AP"]) || isPresent(tariffItem["Customs Duty Unit Rate"]) || dutySelection === "PREF"
                        },
                        { 
                          key: 'pal', 
                          label: 'PAL',
                          hasRate: isPresent(tariffItem["PAL Gen"]) || isPresent(tariffItem["PAL"])
                        },
                        { 
                          key: 'pal_sg', 
                          label: 'PAL SG',
                          hasRate: isPresent(tariffItem["PAL SG"])
                        },
                        { 
                          key: 'cess', 
                          label: 'CESS',
                          hasRate: isPresent(tariffItem["Cess Gen"]) || isPresent(tariffItem["Cess"])
                        },
                        { 
                          key: 'cess_sg', 
                          label: 'CESS SG',
                          hasRate: isPresent(tariffItem["Cess SG"])
                        },
                        { 
                          key: 'surcharge', 
                          label: 'SURCHARGE',
                          hasRate: isPresent(tariffItem["Surcharge on Customs Duty"]) || isPresent(tariffItem["Surcharge"])
                        },
                        { 
                          key: 'excise', 
                          label: 'EXCISE',
                          hasRate: isPresent(tariffItem["Excise (S.P.D)"]) || isPresent(tariffItem["Excise"])
                        },
                        { 
                          key: 'vat', 
                          label: 'VAT',
                          hasRate: isPresent(tariffItem["VAT"])
                        },
                        { 
                          key: 'sscl', 
                          label: 'SSCL',
                          hasRate: isPresent(tariffItem["SSCL"])
                        },
                        { 
                          key: 'scl', 
                          label: 'SCL',
                          hasRate: isPresent(tariffItem["S C L"]) || isPresent(tariffItem["SCL"])
                        },
                        { 
                          key: 'luxury_tax', 
                          label: 'LUXURY TAX',
                          hasRate: isPresent(tariffItem["Luxury Tax_Free threshold value"]) || isPresent(tariffItem["Luxury Tax_Rate on the amount exceeding threshold"]) || isPresent(tariffItem["Luxury Tax Free"])
                        },
                      ].filter((item) => {
                        const key = item.key as keyof CalculatedLevies;
                        const value = (calculatedLevies[key] || 0) as number;
                        // Show all not null levies (present in tariff rates) or with calculated value > 0
                        return item.hasRate || value > 0;
                      }).map((item) => {
                        const key = item.key as keyof CalculatedLevies;
                        const value = (calculatedLevies[key] || 0) as number;
                        const detailKey = `${key}_detail` as keyof CalculatedLevies;
                        const detailValue = calculatedLevies[detailKey];
                        
                        return (
                          <div key={key} className="breakdown-item">
                            <div className="item-info">
                              <span className="item-label">{item.label}</span>
                              {detailValue && <span className="item-detail-text">{detailValue}</span>}
                            </div>
                            <span className="item-value">{value.toLocaleString(undefined, {minimumFractionDigits: 2})} LKR</span>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  <div className="summary-card">
                    <div className="summary-row">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Receipt size={18} color="#64748b" />
                        Total Levies:
                      </span>
                      <span className="text-red">{calculatedLevies.total_levies.toLocaleString(undefined, {minimumFractionDigits: 2})} {LKR_SYMBOL}</span>
                    </div>
                    <div className="summary-row main-total">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <CircleDollarSign size={24} color="#059669" />
                        TOTAL COST:
                      </span>
                      <span className="text-green">{calculatedLevies.total_with_cif.toLocaleString(undefined, {minimumFractionDigits: 2})} {LKR_SYMBOL}</span>
                    </div>
                  </div>

                  <button className="secondary-btn" onClick={() => setIsBreakdownOpen(false)} style={{ width: "100%", marginTop: "1.5rem" }}>
                    Close Breakdown
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="modal-footer">
            <button className="footer-close-button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TariffDetailModal;